import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChartBarIcon as ChartBar,
  DownloadSimpleIcon as DownloadSimple,
  FolderIcon as Folder,
  EyeIcon as Eye,
  ArrowLeftIcon as ArrowLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getPipelineVelocity } from "@/lib/api";

const METRICS = [
  {
    key: "downloads",
    title: "Sample pack downloads",
    subtitle: "Top-of-funnel — prospects pulling our demo source.",
    icon: DownloadSimple,
    field: "sample_pack_downloads",
    dailyField: "sample_pack_downloads_daily",
    csvHeader: "sample_pack_downloads",
  },
  {
    key: "workspaces",
    title: "Workspaces created",
    subtitle: "Mid-funnel — engagements you've actually started.",
    icon: Folder,
    field: "workspaces_created",
    dailyField: "workspaces_created_daily",
    csvHeader: "workspaces_created",
  },
  {
    key: "opens",
    title: "Client share-link opens",
    subtitle: "Bottom-funnel — clients clicking through to the deliverables.",
    icon: Eye,
    field: "client_share_opens",
    dailyField: "client_share_opens_daily",
    csvHeader: "client_share_opens",
  },
];

const WINDOWS = [30, 60, 90];

export default function Telemetry() {
  const [params, setParams] = useSearchParams();
  const initialMetric = METRICS.find((m) => m.key === params.get("metric"))?.key || "downloads";
  const initialDays = WINDOWS.includes(Number(params.get("days"))) ? Number(params.get("days")) : 30;
  const [metricKey, setMetricKey] = useState(initialMetric);
  const [days, setDays] = useState(initialDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPipelineVelocity(days).then((d) => {
      setData(d);
      setLoading(false);
    });
  }, [days]);

  // Keep URL in sync so refreshes / bookmarks restore the chosen view.
  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("metric", metricKey);
    next.set("days", String(days));
    setParams(next, { replace: true });
  }, [metricKey, days]);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  // Memoize these so the useMemo below has a stable dependency reference —
  // otherwise `[].length === 0` triggers a fresh array per render and the
  // stats memo loses its caching benefit.
  const dailyValues = useMemo(
    () => (data && data[metric.dailyField]) || [],
    [data, metric.dailyField]
  );
  const totalCount = (data && data[metric.field]) || 0;

  const stats = useMemo(() => {
    if (!dailyValues.length) return { peak: 0, avg: 0, peakDay: null, nonZero: 0 };
    const peak = Math.max(...dailyValues);
    const peakIdx = dailyValues.lastIndexOf(peak);
    const avg = totalCount / dailyValues.length;
    const nonZero = dailyValues.filter((v) => v > 0).length;
    return { peak, avg, peakDay: peakIdx, nonZero };
  }, [dailyValues, totalCount]);

  const handleExportCsv = () => {
    if (!data) return;
    const rows = [];
    rows.push(["date", ...METRICS.map((m) => m.csvHeader)].join(","));
    // Reconstruct dates: oldest → newest, length === days.
    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    for (let i = 0; i < days; i += 1) {
      const d = new Date(now);
      d.setUTCDate(now.getUTCDate() - (days - 1 - i));
      const iso = d.toISOString().slice(0, 10);
      const cells = [iso];
      for (const m of METRICS) {
        cells.push(String((data[m.dailyField] || [])[i] ?? 0));
      }
      rows.push(cells.join(","));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `modernization-lab-telemetry-${days}d.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${days}-day telemetry CSV.`);
  };

  return (
    <div className="space-y-8" data-testid="telemetry-page">
      <div className="flex items-start justify-between gap-6">
        <div>
          <Link
            to="/workspaces"
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] flex items-center gap-1"
            data-testid="back-to-workspaces"
          >
            <ArrowLeft size={11} weight="bold" /> Back to workspaces
          </Link>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mt-3 mb-1">
            Telemetry · pipeline visibility
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold leading-tight tracking-tight">
            <ChartBar size={32} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Demo-to-deal funnel.
          </h1>
          <p className="text-[var(--ml-text-secondary)] max-w-2xl mt-3 leading-relaxed">
            Quarterly-review-ready signals. Drill into any of the three funnel stages and
            export the daily series as CSV for your own pivot tables.
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={!data}
          className="ml-btn-primary shrink-0 flex items-center gap-2"
          data-testid="export-csv-btn"
        >
          <DownloadSimple size={14} weight="bold" />
          Export CSV
        </button>
      </div>

      {/* Metric tabs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="metric-tabs">
        {METRICS.map((m) => {
          const Icon = m.icon;
          const active = m.key === metricKey;
          const total = (data && data[m.field]) || 0;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetricKey(m.key)}
              data-testid={`metric-tab-${m.key}`}
              data-active={active}
              className={`text-left px-5 py-4 border transition-colors ${
                active
                  ? "border-[var(--ml-primary)] bg-[var(--ml-primary)]/5"
                  : "border-[var(--ml-border)] bg-white hover:border-[var(--ml-primary)]/40"
              }`}
            >
              <div className="flex items-center gap-2 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                <Icon size={12} weight="bold" />
                {m.title}
              </div>
              <div className="text-3xl font-semibold mt-1">{total}</div>
              <div className="text-xs text-[var(--ml-text-secondary)] mt-1">{m.subtitle}</div>
            </button>
          );
        })}
      </div>

      {/* Window selector */}
      <div className="flex items-center gap-2" data-testid="window-selector">
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mr-2">
          Window
        </span>
        {WINDOWS.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            data-testid={`window-${d}d`}
            data-active={d === days}
            className={`ml-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
              d === days
                ? "border-[var(--ml-text-primary)] bg-[var(--ml-surface)]"
                : "border-[var(--ml-border)] bg-white hover:border-[var(--ml-text-primary)]/40"
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Big chart */}
      <div className="ml-card p-5" data-testid="telemetry-chart-card">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-4 flex items-center justify-between">
          <span>{metric.title} · last {days}d (oldest → newest)</span>
          <span data-testid="chart-summary">
            Peak {stats.peak} · avg {stats.avg.toFixed(1)}/day · {stats.nonZero}/{days} active days
          </span>
        </div>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]">
            Loading…
          </div>
        ) : (
          <BigBarChart values={dailyValues} days={days} />
        )}
      </div>
    </div>
  );
}

function BigBarChart({ values, days }) {
  // Plain SVG; we use a 1000×180 viewBox and scale via preserveAspectRatio.
  const W = 1000;
  const H = 180;
  const PAD_L = 8;
  const PAD_R = 8;
  const PAD_TOP = 12;
  const PAD_BOTTOM = 24;
  if (!values || values.length === 0) return null;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_TOP - PAD_BOTTOM;
  const max = Math.max(...values, 1);
  const gap = Math.max(1, Math.min(3, Math.floor(innerW / values.length / 10)));
  const barW = (innerW - gap * (values.length - 1)) / values.length;
  // Label every 5th day along the x-axis so 30/60/90-day windows stay readable.
  const everyNth = days <= 30 ? 5 : days <= 60 ? 10 : 15;
  // Reconstruct ISO dates (oldest → newest) for the x-axis labels and tooltip.
  const now = new Date();
  now.setUTCHours(0, 0, 0, 0);
  const labels = values.map((_, i) => {
    const d = new Date(now);
    d.setUTCDate(now.getUTCDate() - (values.length - 1 - i));
    return d.toISOString().slice(5, 10); // MM-DD
  });
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-48 block"
      data-testid="telemetry-bar-chart"
      role="img"
      aria-label={`Daily counts for ${days}-day window`}
    >
      {values.map((v, i) => {
        const h = v === 0 ? 1 : Math.max(2, (v / max) * innerH);
        const x = PAD_L + i * (barW + gap);
        const y = PAD_TOP + innerH - h;
        const isToday = i === values.length - 1;
        return (
          <g key={`bar-${i}`}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={v === 0 ? "#e5e7eb" : (isToday ? "#0050e6" : "#0f62fe")}
              data-testid={`bar-day-${i}`}
            >
              <title>{`${labels[i]} · ${v}`}</title>
            </rect>
            {(i % everyNth === 0 || i === values.length - 1) && (
              <text
                x={x + barW / 2}
                y={H - 6}
                textAnchor="middle"
                fontSize="10"
                fontFamily='"IBM Plex Mono", monospace'
                fill="#6e6e6e"
              >
                {labels[i]}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
